import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { getPunishments, createPunishment, completePunishment, appealPunishment, reviewPunishment, deletePunishment, extractErrorMessage } from '../services/api';
import { FaPlus, FaCheck, FaGavel, FaTrash, FaExclamationTriangle } from 'react-icons/fa';
import UserSelect from '../components/UserSelect';
import { useToast } from '../components/Toast';
import '../styles/Punishments.css';

function Punishments({ user }) {
  const toast = useToast();
  const [punishments, setPunishments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [newPunishment, setNewPunishment] = useState({
    user_id: '',
    type: 'task',
    severity: 'light',
    reason: '',
    description: '',
    deadline: ''
  });

  useEffect(() => {
    fetchPunishments();
  }, [filter]);

  const fetchPunishments = async () => {
    try {
      setLoading(true);
      const data = await getPunishments({ status: filter !== 'all' ? filter : undefined });
      setPunishments(data.punishments || data);
    } catch (error) {
      console.error('Error fetching punishments:', error);
      toast.error(extractErrorMessage(error, 'Strafen konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setFormError(null);
    try {
      await createPunishment({
        ...newPunishment,
        deadline: newPunishment.deadline ? new Date(newPunishment.deadline).toISOString() : null
      });
      setNewPunishment({ user_id: '', type: 'task', severity: 'light', reason: '', description: '', deadline: '' });
      setShowModal(false);
      toast.success('Strafe erstellt');
      fetchPunishments();
    } catch (error) {
      console.error('Error creating punishment:', error);
      setFormError(extractErrorMessage(error, 'Fehler beim Erstellen der Strafe'));
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async (id) => {
    try {
      await completePunishment(id, {});
      toast.success('Strafe als erledigt markiert');
      fetchPunishments();
    } catch (error) {
      console.error('Error completing punishment:', error);
      toast.error(extractErrorMessage(error, 'Fehler beim Abschließen der Strafe'));
    }
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setFormError(null);
  };

  const handleAppeal = async (id) => {
    const reason = prompt('Grund für den Einspruch:');
    if (reason === null) return;
    try {
      await appealPunishment(id, { reason });
      fetchPunishments();
    } catch (error) {
      console.error('Error appealing punishment:', error);
    }
  };

  const handleReview = async (id, approved) => {
    try {
      await reviewPunishment(id, { approved });
      fetchPunishments();
    } catch (error) {
      console.error('Error reviewing punishment:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Strafe wirklich löschen?')) return;
    try {
      await deletePunishment(id);
      fetchPunishments();
    } catch (error) {
      console.error('Error deleting punishment:', error);
    }
  };

  const getSeverityBadge = (severity) => {
    const badges = {
      light: { class: 'light', label: 'Leicht' },
      medium: { class: 'medium', label: 'Mittel' },
      severe: { class: 'severe', label: 'Schwer' },
      extreme: { class: 'extreme', label: 'Extrem' }
    };
    return badges[severity] || badges.light;
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { class: 'active', label: 'Aktiv' },
      completed: { class: 'completed', label: 'Erledigt' },
      appealed: { class: 'appealed', label: 'Einspruch' },
      cancelled: { class: 'cancelled', label: 'Aufgehoben' }
    };
    return badges[status] || badges.active;
  };

  return (
    <div className="app-layout">
      <Navbar user={user} />
      <main className="main-content">
        <div className="page-header">
          <h1>Strafen</h1>
          {user.isAdmin && (
            <button className="add-btn" onClick={() => setShowModal(true)}>
              <FaPlus /> Neue Strafe
            </button>
          )}
        </div>

        <div className="filter-tabs">
          <button className={filter === 'all' ? 'active' : ''} onClick={() => setFilter('all')}>Alle</button>
          <button className={filter === 'active' ? 'active' : ''} onClick={() => setFilter('active')}>Aktiv</button>
          <button className={filter === 'completed' ? 'active' : ''} onClick={() => setFilter('completed')}>Erledigt</button>
          <button className={filter === 'appealed' ? 'active' : ''} onClick={() => setFilter('appealed')}>Einspruch</button>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : (
          <div className="punishments-table-wrapper">
            {punishments.length > 0 ? (
              <table className="punishments-table">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Schwere</th>
                    <th>Typ</th>
                    <th>Grund</th>
                    <th>Beschreibung</th>
                    <th>Frist</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {punishments.map(p => {
                    const sevBadge = getSeverityBadge(p.severity);
                    const statusBadge = getStatusBadge(p.status);
                    return (
                      <tr key={p.id}>
                        <td><span className={`status-badge ${statusBadge.class}`}>{statusBadge.label}</span></td>
                        <td><span className={`severity-badge ${sevBadge.class}`}>{sevBadge.label}</span></td>
                        <td className="type-cell">{p.type}</td>
                        <td className="reason-cell">{p.reason}</td>
                        <td className="desc-cell">{p.description}</td>
                        <td>{p.deadline ? new Date(p.deadline).toLocaleDateString() : '-'}</td>
                        <td>
                          <div className="table-actions">
                            {p.status === 'active' && (
                              <>
                                <button className="action-btn complete" onClick={() => handleComplete(p.id)} title="Erledigt">
                                  <FaCheck />
                                </button>
                                <button className="action-btn appeal" onClick={() => handleAppeal(p.id)} title="Einspruch">
                                  <FaExclamationTriangle />
                                </button>
                              </>
                            )}
                            {p.status === 'appealed' && user.isAdmin && (
                              <>
                                <button className="action-btn approve" onClick={() => handleReview(p.id, true)} title="Einspruch genehmigen">
                                  <FaCheck />
                                </button>
                                <button className="action-btn reject" onClick={() => handleReview(p.id, false)} title="Einspruch ablehnen">
                                  <FaGavel />
                                </button>
                              </>
                            )}
                            {user.isAdmin && (
                              <button className="action-btn delete" onClick={() => handleDelete(p.id)} title="Löschen">
                                <FaTrash />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="no-data">Keine Strafen gefunden</div>
            )}
          </div>
        )}

        {showModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>Neue Strafe</h2>
              {formError && <div className="shared-form-error-banner">{formError}</div>}
              <form onSubmit={handleCreate}>
                <div className="form-group">
                  <UserSelect
                    value={newPunishment.user_id}
                    onChange={id => setNewPunishment({...newPunishment, user_id: id})}
                    required
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Typ</label>
                    <select
                      value={newPunishment.type}
                      onChange={e => setNewPunishment({...newPunishment, type: e.target.value})}
                    >
                      <option value="task">Aufgabe</option>
                      <option value="restriction">Einschränkung</option>
                      <option value="writing">Schreiben</option>
                      <option value="other">Sonstiges</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Schwere</label>
                    <select
                      value={newPunishment.severity}
                      onChange={e => setNewPunishment({...newPunishment, severity: e.target.value})}
                    >
                      <option value="light">Leicht</option>
                      <option value="medium">Mittel</option>
                      <option value="severe">Schwer</option>
                      <option value="extreme">Extrem</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>Grund *</label>
                  <input
                    type="text"
                    value={newPunishment.reason}
                    onChange={e => setNewPunishment({...newPunishment, reason: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Beschreibung</label>
                  <textarea
                    value={newPunishment.description}
                    onChange={e => setNewPunishment({...newPunishment, description: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Frist</label>
                  <input
                    type="datetime-local"
                    value={newPunishment.deadline}
                    onChange={e => setNewPunishment({...newPunishment, deadline: e.target.value})}
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeModal} disabled={saving}>Abbrechen</button>
                  <button type="submit" className="submit-btn" disabled={saving}>{saving ? 'Speichert…' : 'Erstellen'}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Punishments;
